import { useCallback, useEffect, useRef, useState } from "react";

const ATTENTION_VISIBLE_MS = 8000;

/**
 * Shows a short-lived nudge beside the context-agent launcher when scope registers.
 * Shows once per scope — not again after chat open, dismiss, or auto-hide.
 */
export default function useContextAgentAttentionBubble({
  scopeKey,
  open,
  messageReady = true,
  onOpenChat,
}) {
  const [phase, setPhase] = useState("hidden");
  const scopeKeyRef = useRef(scopeKey);
  const consumedRef = useRef(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (scopeKey !== scopeKeyRef.current) {
      scopeKeyRef.current = scopeKey;
      consumedRef.current = false;
    }
  }, [scopeKey]);

  useEffect(() => {
    if (!scopeKey || !messageReady) {
      setPhase("hidden");
      return undefined;
    }

    if (open) {
      consumedRef.current = true;
      setPhase("hidden");
      return undefined;
    }

    if (consumedRef.current) {
      setPhase("hidden");
      return undefined;
    }

    setPhase("visible");
    hideTimerRef.current = window.setTimeout(() => {
      if (consumedRef.current) {
        return;
      }
      consumedRef.current = true;
      setPhase("exiting");
    }, ATTENTION_VISIBLE_MS);

    return () => window.clearTimeout(hideTimerRef.current);
  }, [scopeKey, open, messageReady]);

  const handleBubbleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      consumedRef.current = true;
      window.clearTimeout(hideTimerRef.current);
      setPhase("hidden");
      onOpenChat();
    },
    [onOpenChat]
  );

  const handleBubbleDismiss = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    consumedRef.current = true;
    window.clearTimeout(hideTimerRef.current);
    setPhase("exiting");
  }, []);

  const handleBubbleAnimationEnd = useCallback((event) => {
    if (event.currentTarget !== event.target) {
      return;
    }
    if (
      event.animationName === "context-agent-attention-out" ||
      event.animationName === "context-agent-attention-out-end"
    ) {
      consumedRef.current = true;
      setPhase("hidden");
    }
  }, []);

  return {
    bubbleVisible: phase === "visible" || phase === "exiting",
    bubbleExiting: phase === "exiting",
    handleBubbleClick,
    handleBubbleDismiss,
    handleBubbleAnimationEnd,
  };
}
